import { formatCpf, formatCnpj } from '@brazilian-utils/formatters';
import IMask from 'imask';
import moment from 'moment';

export { formatCnpj };
export { formatCpf };

export const getOnlyNumbers = (value = '') => String(value).replace(/\D/g, '');

export const formatMobilePhone = (value = '') => {
  const mobileMask = IMask.createMask({ mask: '(00) 00000-0000' });

  return mobileMask.resolve(getOnlyNumbers(value));
};

export const formatCommercialPhone = (value = '') => {
  const phoneMask = IMask.createMask({ mask: '(00) 00000-0000' });

  return phoneMask.resolve(getOnlyNumbers(value));
};

export const formatCpfCnpj = (value = '') => {
  const onlyNumbers = getOnlyNumbers(value);

  if (onlyNumbers.length <= 11) {
    return formatCpf(onlyNumbers);
  }

  return formatCnpj(onlyNumbers);
};

export const formatCep = (value = '') => {
  const cepMask = IMask.createMask({ mask: '00000-000' });
  const onlyNumbers = getOnlyNumbers(value);
  return cepMask.resolve(onlyNumbers);
};

export const getMonetaryValue = (value, onlyShow = false, precision = 2) => {
  let isNegative = false;
  if (value.includes('-') && value !== '-') {
    isNegative = true;
  }
  let _value = '';

  if (typeof value === 'number') {
    _value = String(value.toFixed(precision));
  } else if (onlyShow) {
    _value = value.replace(/[^\d.-]/g, '');
  } else {
    _value = value.replace(/\D/g, '');
  }

  if (value.length < precision) {
    _value = `0.${Array(precision - value.length)
      .fill()
      .map(() => '0')
      .join('')}${_value}`;
  } else {
    _value = `${_value.slice(0, -precision)}.${_value.slice(-precision)}`;
  }

  if (isNegative) {
    return parseFloat(-(_value || 1), 10);
  }
  return parseFloat(_value, 10);
};

// Cache para o símbolo da moeda (será invalidado quando a moeda mudar)
let currencySymbolCache = null;
let currencySymbolCacheTimestamp = 0;

// Função para obter o símbolo de moeda do usuário
export const getUserCurrency = (forceRefresh = false) => {
  // Se não forçar refresh e o cache for recente (menos de 1 segundo), usar cache
  if (!forceRefresh && currencySymbolCache && Date.now() - currencySymbolCacheTimestamp < 1000) {
    return currencySymbolCache;
  }

  if (typeof window !== 'undefined') {
    try {
      const state = require('../redux/storeConfig/store').store.getState();
      // O usuário está armazenado em loggedInUser, não em user
      const user = state?.auth?.login?.values?.loggedInUser || state?.auth?.login?.values?.user || state?.auth?.user;
      
      // Se o usuário tem currency definida, usar o símbolo dela
      if (user?.currency?.symbol) {
        currencySymbolCache = user.currency.symbol;
        currencySymbolCacheTimestamp = Date.now();
        return user.currency.symbol;
      }
      
      // Se currency_id for NULL (padrão), usar $
      currencySymbolCache = '$';
      currencySymbolCacheTimestamp = Date.now();
      return '$';
    } catch (e) {
      // Ignorar erro
      console.warn('Erro ao obter moeda do usuário:', e);
    }
    
    // Fallback para localStorage (caso tenha sido salvo anteriormente)
    const savedSymbol = localStorage.getItem('iuli_currency_symbol');
    if (savedSymbol) {
      currencySymbolCache = savedSymbol;
      currencySymbolCacheTimestamp = Date.now();
      return savedSymbol;
    }
  }
  
  // Padrão: $ (quando currency_id é NULL)
  currencySymbolCache = '$';
  currencySymbolCacheTimestamp = Date.now();
  return '$';
};

// Função para invalidar o cache (chamada quando a moeda muda)
export const invalidateCurrencyCache = () => {
  currencySymbolCache = null;
  currencySymbolCacheTimestamp = 0;
};

// Escutar evento de mudança de moeda para invalidar cache
if (typeof window !== 'undefined') {
  window.addEventListener('currencyChanged', () => {
    invalidateCurrencyCache();
    // Forçar re-render de componentes que usam formatMoney
    window.dispatchEvent(new CustomEvent('currencyFormatUpdate'));
  });
}

// Função para formatar moeda sempre em R$ (usada em links de pagamento)
export const formatMoneyBRL = (
  value,
  onlyShow = false,
  { currency, locale } = { currency: 'BRL', locale: 'pt-br' }
) => {
  if (value === undefined || value === null) {
    return '';
  }
  const _value = typeof value === 'number' ? value.toFixed(2) : value;

  const monetaryValue = onlyShow
    ? parseFloat(_value)
    : getMonetaryValue(_value.replace(/[^\d-]/g, ''));

  let formattedValue;
  if (currency && locale) {
    formattedValue = monetaryValue.toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    formattedValue = monetaryValue.toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Garantir que sempre use R$ (remover qualquer outro símbolo e adicionar R$)
  formattedValue = formattedValue.replace(/R\$\s*/g, ''); // Remove R$ se já existir
  formattedValue = formattedValue.replace(/\$\s*/g, ''); // Remove $
  formattedValue = formattedValue.replace(/€\s*/g, ''); // Remove €
  formattedValue = formattedValue.replace(/£\s*/g, ''); // Remove £
  formattedValue = formattedValue.replace(/¥\s*/g, ''); // Remove ¥
  formattedValue = formattedValue.replace(/₹\s*/g, ''); // Remove ₹
  formattedValue = formattedValue.replace(/₲\s*/g, ''); // Remove ₲
  formattedValue = formattedValue.replace(/\$U\s*/g, ''); // Remove $U
  formattedValue = formattedValue.replace(/C\$\s*/g, ''); // Remove C$
  formattedValue = formattedValue.replace(/A\$\s*/g, ''); // Remove A$
  formattedValue = formattedValue.replace(/CHF\s*/g, ''); // Remove CHF
  
  // Sempre adicionar R$ no início
  formattedValue = `R$ ${formattedValue.trim()}`;
  
  return formattedValue;
};

export const formatMoney = (
  value,
  onlyShow = false,
  { currency, locale } = { currency: 'BRL', locale: 'pt-br' }
) => {
  if (value === undefined || value === null) {
    return '';
  }
  const _value = typeof value === 'number' ? value.toFixed(2) : value;

  const monetaryValue = onlyShow
    ? parseFloat(_value)
    : getMonetaryValue(_value.replace(/[^\d-]/g, ''));

  // Obter símbolo do usuário (forçar refresh para pegar o mais atual)
  const userSymbol = getUserCurrency(true);
  
  let formattedValue;
  if (currency && locale) {
    formattedValue = monetaryValue.toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    formattedValue = monetaryValue.toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Substituir o símbolo padrão pelo símbolo escolhido pelo usuário
  if (userSymbol) {
    // Padrões comuns de formatação de moeda:
    // "R$ 1.234,56" ou "R$1.234,56" ou "$1.234,56" ou "€ 1.234,56"
    // Substituir qualquer símbolo de moeda pelo escolhido pelo usuário
    
    // Primeiro, remover símbolos conhecidos (R$, $, €, £, ¥, ₹, ₲, $U)
    formattedValue = formattedValue.replace(/R\$\s*/g, ''); // Remove R$
    formattedValue = formattedValue.replace(/\$\s*/g, ''); // Remove $
    formattedValue = formattedValue.replace(/€\s*/g, ''); // Remove €
    formattedValue = formattedValue.replace(/£\s*/g, ''); // Remove £
    formattedValue = formattedValue.replace(/¥\s*/g, ''); // Remove ¥
    formattedValue = formattedValue.replace(/₹\s*/g, ''); // Remove ₹
    formattedValue = formattedValue.replace(/₲\s*/g, ''); // Remove ₲
    formattedValue = formattedValue.replace(/\$U\s*/g, ''); // Remove $U
    formattedValue = formattedValue.replace(/C\$\s*/g, ''); // Remove C$
    formattedValue = formattedValue.replace(/A\$\s*/g, ''); // Remove A$
    formattedValue = formattedValue.replace(/CHF\s*/g, ''); // Remove CHF
    
    // Adicionar o símbolo escolhido pelo usuário no início
    formattedValue = `${userSymbol} ${formattedValue.trim()}`;
  }
  
  return formattedValue;
};

export const formatAliquot = (value, onlyShow = false) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const _value = typeof value === 'number' ? value.toFixed(4) : value;

  const monetaryValue = onlyShow
    ? parseFloat(_value)
    : getMonetaryValue(_value.replace(/\D/g, ''), false, 4);

  return monetaryValue.toLocaleString('pt-br', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

export const getAliquotValue = (value) => getMonetaryValue(value, false, 4);

export const formatPercentage = (value, onlyShow = false, digits = 2) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const _value = typeof value === 'number' ? value.toFixed(digits) : value;

  const monetaryValue = onlyShow
    ? parseFloat(_value)
    : getMonetaryValue(_value.replace(/\D/g, ''), false, digits);

  return monetaryValue.toLocaleString('pt-br', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const getPercentageValue = (value, digits = 2) =>
  getMonetaryValue(value, false, digits);

export const formatDateToString = (dateOriginal) => {
  const date = dateOriginal.getDate();
  const month = dateOriginal.getMonth();
  const year = dateOriginal.getFullYear();

  const mm = String(month + 1).padStart(2, '0');
  const dd = String(date).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

export const formatDateToHumanString = (dateOriginal) => {
  if (!dateOriginal) return '';
  
  // Extract just the date part (YYYY-MM-DD) from any format using regex
  // This avoids timezone conversion issues that cause dates to appear 1 day off
  // Works with: "2026-01-06", "2026-01-06T00:00:00.000000Z", "2026-01-06T12:00:00", etc.
  const dateStr = String(dateOriginal);
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }
  
  // Fallback for unexpected formats (Date objects, moment objects, etc.)
  return moment(dateOriginal).format('DD/MM/YYYY');
};

export const getDateFromFlatpickr = (formikValue) => {
  if (typeof formikValue === 'string') {
    return formikValue;
  }

  return formikValue?.[0] ? formatDateToString(formikValue[0]) : null;
};

export const parseLocalYMDToDate = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const [yearStr, monthStr, dayStr] = ymd.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // 0-based
  const day = parseInt(dayStr, 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month, day);
};
